import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faBolt, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { aiRecommendation, aiScores, cleanItems, orderDevelopments } from '../utils/aiAnalysisDisplay';
import { ratingScaleStyle } from '../utils/ratingScale';
import { ACCENT } from '../constants/uiColors';

// Sections of the stock profile that render the stored AI analysis (issue #445).
// Plain CSS classes (AiAnalysisPanel.css) and inline styles only: the panel's
// older rules lean on Tailwind utilities and `@apply`, neither of which this
// build generates, which is why the old lists rendered with no bullets.

// The heading every profile section card uses: icon + title, with an optional
// right-aligned aside (an "as of" stamp). `iconColor={null}` lets the icon take
// the title's colour from CSS (the accent-topped list cards).
export const SectionHeading = ({ icon, iconColor = ACCENT, aside, children }) => (
  <div className="ai-section-head">
    <h3 className="text-xl font-semibold ai-section-title">
      {icon && <FontAwesomeIcon icon={icon} style={iconColor ? { color: iconColor } : undefined} />}
      {children}
    </h3>
    {aside}
  </div>
);

// The AI's Strong Buy … Strong Sell call, coloured on the shared 1–5 ladder.
// ONE renderer for the AI Assessment card's pill and the Company Snapshot tile
// (issue #451); `className` lets the card draw it as a bordered pill.
export const AiCallText = ({ recommendation, className }) => (
  <span className={className} style={ratingScaleStyle(recommendation.rank)}>
    {recommendation.label}
  </span>
);

const AsOfStamp = ({ asOf, prefix }) => (
  <span className="ai-asof" title={`Analysis written ${asOf.label} (${asOf.age})`}>
    {prefix} {asOf.label} · {asOf.age}
  </span>
);

const ScoreRow = ({ score: { label, value, higherIsWorse }, note }) => (
  <div
    className={`ai-score-row${higherIsWorse ? ' ai-score-row--inverse' : ''}`}
    title={higherIsWorse ? 'Scored 1–10 by the AI model; higher means more speculative' : 'Scored 1–10 by the AI model; higher is more favourable'}
  >
    <span className="ai-score-label">
      {label}
      {note && <span className="ai-score-note">{note}</span>}
    </span>
    <span className="ai-score-track">
      <span className="ai-score-fill" style={{ width: `${value * 10}%` }} />
    </span>
    <span className="ai-score-value">{value}</span>
  </div>
);

// The model's recommendation, its one-line reason and its 1–10 scores —
// stored on every analysis since the panel was added, rendered by nothing.
export const AiAssessment = ({ aiAnalysis, asOf }) => {
  const recommendation = aiRecommendation(aiAnalysis?.recommendation_rating);
  const scores = aiScores(aiAnalysis?.summary_rating);
  const explanation = typeof aiAnalysis?.recommendation_explanation === 'string'
    ? aiAnalysis.recommendation_explanation.trim()
    : '';
  if (!recommendation && scores.length === 0) return null;
  const favourable = scores.filter((score) => !score.higherIsWorse);
  const inverse = scores.filter((score) => score.higherIsWorse);

  return (
    <section className="bg-white rounded-lg shadow p-6 mb-4">
      <SectionHeading icon={faRobot} aside={asOf && <AsOfStamp asOf={asOf} prefix="AI opinion ·" />}>
        AI Assessment
      </SectionHeading>

      <div className="ai-assessment-grid">
        {recommendation && (
          <div>
            <AiCallText recommendation={recommendation} className="ai-rec-pill" />
            {explanation && <p className="ai-rec-text">{explanation}</p>}
          </div>
        )}

        {scores.length > 0 && (
          <div className="ai-scores">
            {favourable.map((score) => <ScoreRow key={score.key} score={score} />)}
            {/* Speculation runs the other way (10 = mostly hype), so it sits
                apart, in amber, with its direction spelled out (issue #446). */}
            {inverse.map((score) => (
              <ScoreRow key={score.key} score={score} note="higher = more speculative" />
            ))}
          </div>
        )}
      </div>

      <p className="ai-note">
        Scores are 1–10, higher is more favourable{inverse.length > 0 && ' — except Speculation / hype, where higher means more of the price rests on narrative rather than fundamentals'}.
        Generated by an AI model from the data available to it — an opinion, not investment advice.
      </p>
    </section>
  );
};

// Key Developments beside Risk Factors. Developments are dated where the model
// wrote a date (pulled out of the sentence into a chip, newest first), and the
// card states when the analysis was written — "recent" is only as recent as
// that, and pre-2026-09 analyses were often written from model memory alone.
export const DevelopmentsAndRisks = ({ analysisData, asOf }) => {
  const developments = orderDevelopments(analysisData?.key_developments);
  const risks = cleanItems(analysisData?.risk_factors);
  if (developments.length === 0 && risks.length === 0) return null;

  const hasDates = developments.some((item) => item.date);

  return (
    <div className="ai-list-grid">
      {developments.length > 0 && (
        <section className="ai-list-card ai-list-card--developments">
          <SectionHeading icon={faBolt} iconColor={null} aside={asOf && <AsOfStamp asOf={asOf} prefix="as of" />}>
            Key Developments
          </SectionHeading>
          <ul className={`ai-list${hasDates ? ' ai-list--dated' : ''}`}>
            {developments.map((item) => (
              <li key={item.index} className="ai-list-item">
                {item.date
                  ? <span className="ai-date-chip">{item.date.label}</span>
                  : <span className="ai-dot" aria-hidden="true" />}
                <span>{item.text}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {risks.length > 0 && (
        <section className="ai-list-card ai-list-card--risks">
          <SectionHeading icon={faTriangleExclamation} iconColor={null}>
            Risk Factors
          </SectionHeading>
          <ul className="ai-list">
            {risks.map((risk, index) => (
              <li key={index} className="ai-list-item">
                <span className="ai-dot" aria-hidden="true" />
                <span>{risk}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};
